import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  Camera,
  CheckCircle2,
  CircleDot,
  Loader2,
  Power,
  RefreshCw,
  Search,
  SendHorizonal,
  ShieldCheck,
  ShieldQuestion,
  Square,
  Terminal,
  Wrench,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  cn,
} from '@staff-app/ui';
import { isEnterKey } from '@/lib/keyboard';
import { formatDateTimeWithSeconds } from '@/lib/format-date';
import {
  ConnState,
  EventType,
  TimelineKind,
  ToolStatus,
  sessionLabel,
  useDeviceSessionStream,
  type ApprovalRequestPayload,
  type DeviceSessionOutput,
  type SessionEvent,
  type TimelineItem,
} from '../hooks/useDeviceSessionStream';
import { ToolStepBody } from './ToolStepBody';

// Live device-troubleshoot session view — vendored + adapted (English literals, @staff-app/ui) from scio3's
// OptiBot DeviceTroubleshootSessionView. Staff differences: the operator is ALWAYS the approver (no customer
// in the loop), the approval card shows the full params + paramsHash the grant is bound to, and there is no
// resume — a terminal state (ended / token expired) hands off to the recorded transcript.

const CONN_LABEL: Record<ConnState, string> = {
  [ConnState.Connecting]: 'Connecting',
  [ConnState.Live]: 'Live',
  [ConnState.Reconnecting]: 'Reconnecting',
  [ConnState.Ended]: 'Ended',
  [ConnState.Error]: 'Disconnected',
};

/** `mcp__device__device__read` → `read`, `device__exec` → `exec`. */
function toolKey(raw?: string): string {
  const stripped = (raw ?? '')
    .replace(/^mcp__device__/, '')
    .replace(/^mcp__[^_]+__/, '')
    .replace(/^device__/, '');
  return stripped || raw || 'tool';
}

/** KB / bookkeeping steps the agent narrates instead of surfacing (same set as the transcript view). */
const HIDDEN_TOOL_KEYS = new Set([
  'server_record',
  'searchInternalDocs',
  'readInternalDoc',
  'searchArticles',
  'readArticle',
]);

const TOOL_ICON: Record<string, typeof Activity> = {
  read: Search,
  exec: Terminal,
  screenshot: Camera,
};

const approvalOf = (e: SessionEvent) => e.payload as ApprovalRequestPayload;

interface Props {
  session: DeviceSessionOutput;
  /** Fired ONCE when the session is over (ended, or its token expired) — the host refetches the list. */
  onEnded?: () => void;
  /** Open this session's recorded transcript (the terminal-state hand-off). */
  onOpenTranscript?: () => void;
  /** Dismiss the (terminal) live view back to the session list. */
  onClose?: () => void;
}

export function LiveTroubleshootSession({ session, onEnded, onOpenTranscript, onClose }: Props) {
  const {
    events,
    timeline,
    conn,
    busy,
    expired,
    decide,
    interrupt,
    endSession,
    sendMessage,
    reconnect,
  } = useDeviceSessionStream(session, { mode: 'live' });

  const terminal = conn === ConnState.Ended || expired;
  const endedNotified = useRef(false);
  useEffect(() => {
    if (terminal && !endedNotified.current) {
      endedNotified.current = true;
      onEnded?.();
    }
  }, [terminal, onEnded]);

  const granted = useMemo(
    () =>
      new Set(
        events
          .filter((e) => e.type === EventType.ApprovalGrant)
          .map((e) => (e.payload as { toolUseId?: string } | null)?.toolUseId)
          .filter((id): id is string => Boolean(id)),
      ),
    [events],
  );
  const isPending = useCallback(
    (e: SessionEvent) =>
      e.type === EventType.ApprovalRequest &&
      !approvalOf(e).preAuthorized &&
      !granted.has(approvalOf(e).toolUseId),
    [granted],
  );
  const pendingApproval = useMemo(() => events.some(isPending), [events, isPending]);
  // The stream is the only place the write grant is visible to the console: a pre-authorized
  // approval_request means open-api found the account's SecureLink write grant on.
  const autoApproving = useMemo(
    () => events.some((e) => e.type === EventType.ApprovalRequest && approvalOf(e).preAuthorized),
    [events],
  );

  const booting =
    conn === ConnState.Connecting ||
    (conn === ConnState.Live && !busy && !pendingApproval && timeline.length === 0);
  const working = conn === ConnState.Live && busy && !pendingApproval;
  const lastActivitySeq = useMemo(() => events.reduce((m, e) => (e.seq > m ? e.seq : m), 0), [events]);

  const viewportRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  useEffect(() => {
    const el = viewportRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [timeline, booting, working]);

  const onDecide = (req: ApprovalRequestPayload, d: 'allow' | 'deny') =>
    decide(req, d).catch(() => {
      toast.error('Could not send your decision to the device agent.');
      throw new Error('decision failed');
    });

  const label = sessionLabel(session) ?? 'Live troubleshooting';

  return (
    <section
      data-testid="troubleshoot-live-view"
      className="flex min-h-0 flex-col rounded-lg border border-border bg-card"
    >
      <header className="border-b border-border p-3">
        <div className="mb-1 flex items-center gap-2 text-sm font-medium text-foreground">
          <Wrench className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0">{label}</span>
        </div>
        {session.actor ? (
          <div className="mb-2 truncate text-[11px] text-muted-foreground">
            {`Started by ${session.actor} · you approve device changes (the customer is not asked)`}
          </div>
        ) : (
          <div className="mb-2 text-[11px] text-muted-foreground">
            {'You approve device changes (the customer is not asked)'}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge
              data-testid="troubleshoot-conn"
              variant={
                conn === ConnState.Live
                  ? 'success'
                  : conn === ConnState.Error
                    ? 'destructive'
                    : 'secondary'
              }
              className="text-[10px]"
            >
              {expired ? 'Token expired' : CONN_LABEL[conn]}
            </Badge>
            {conn === ConnState.Live && !busy && !pendingApproval ? (
              <span className="text-[10px] text-muted-foreground">{'Idle — waiting for you'}</span>
            ) : null}
            {autoApproving ? (
              <Badge variant="warning" className="text-[10px]" data-testid="troubleshoot-auto-approve">
                {'Write grant on — writes auto-approve'}
              </Badge>
            ) : null}
          </div>
          {conn === ConnState.Live ? (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  void interrupt().catch(() => toast.error('Could not interrupt the agent.'))
                }
              >
                <Square className="mr-1" />
                {'Stop turn'}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive">
                    <Power className="mr-1" />
                    {'End session'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{'End this session?'}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {'The device agent stops and the SecureLink runner is torn down. The transcript stays in the history.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{'Cancel'}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => {
                        endSession().catch(() => toast.error('Could not end the session.'));
                      }}
                    >
                      {'End session'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : null}
          {(conn === ConnState.Error || conn === ConnState.Reconnecting) && !expired ? (
            <Button size="sm" variant="ghost" onClick={reconnect}>
              <RefreshCw className="mr-1" />
              {'Reconnect'}
            </Button>
          ) : null}
        </div>
      </header>

      <div
        ref={viewportRef}
        onScroll={() => {
          const el = viewportRef.current;
          if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
        className="max-h-[55vh] min-h-[160px] overflow-y-auto p-3 scroll-thin"
      >
        <ol className="space-y-2">
          {timeline.map((item) => (
            <TimelineRow
              key={item.id}
              item={item}
              live={conn === ConnState.Live}
              isPending={isPending}
              deviceName={session.deviceName}
              onDecide={onDecide}
            />
          ))}
          {booting || working ? (
            <li>
              <WorkingIndicator phase={booting ? 'booting' : 'working'} resetKey={lastActivitySeq} />
            </li>
          ) : null}
          {timeline.length === 0 && !booting && !working ? (
            <li className="text-xs text-muted-foreground">
              {terminal ? 'No activity was recorded.' : 'Waiting for the device agent…'}
            </li>
          ) : null}
        </ol>
      </div>

      {conn === ConnState.Live ? (
        <SessionComposer onSend={(text) => void sendMessage(text).catch(() => toast.error('Message not sent.'))} />
      ) : null}

      {terminal ? (
        <div
          data-testid="troubleshoot-live-terminal"
          className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-3 text-xs text-muted-foreground"
        >
          <span>
            {expired
              ? 'The live-session token expired (30 min). The session’s record is in the history.'
              : 'This session has ended.'}
          </span>
          <div className="flex gap-1">
            {onOpenTranscript ? (
              <Button size="sm" variant="outline" onClick={onOpenTranscript}>
                {'View transcript'}
              </Button>
            ) : null}
            {onClose ? (
              <Button size="sm" variant="ghost" onClick={onClose}>
                {'Back to sessions'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function TimelineRow({
  item,
  live,
  isPending,
  deviceName,
  onDecide,
}: {
  item: TimelineItem;
  live: boolean;
  isPending: (e: SessionEvent) => boolean;
  deviceName?: string;
  onDecide: (req: ApprovalRequestPayload, d: 'allow' | 'deny') => Promise<void>;
}) {
  if (item.kind === TimelineKind.Message) {
    return (
      <li title={formatDateTimeWithSeconds(item.ts)}>
        <AgentMessage text={item.text} streaming={!item.final && live} />
      </li>
    );
  }
  if (item.kind === TimelineKind.Tool) {
    return (
      <li title={formatDateTimeWithSeconds(item.ts)}>
        <ToolStepRow item={item} live={live} />
      </li>
    );
  }
  const ev = item.event;
  switch (ev.type) {
    case EventType.UserMessage: {
      const text = summarize(ev);
      return text ? (
        <li className="flex justify-end" title={formatDateTimeWithSeconds(ev.ts)}>
          <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground">
            {text}
          </div>
        </li>
      ) : null;
    }
    case EventType.UserInterrupt:
      return (
        <li className="flex items-center justify-center gap-1.5 py-1 text-[11px] text-muted-foreground">
          <Square className="h-3 w-3" />
          {'Turn interrupted'}
        </li>
      );
    case EventType.ApprovalRequest: {
      const req = approvalOf(ev);
      if (isPending(ev)) {
        return (
          <li>
            {live ? (
              <ApprovalCard req={req} deviceName={deviceName} onDecide={(d) => onDecide(req, d)} />
            ) : (
              <AuditRow icon={ShieldQuestion} label="Change not approved" command={req.command} reason={req.reason} muted />
            )}
          </li>
        );
      }
      return (
        <li title={formatDateTimeWithSeconds(ev.ts)}>
          <AuditRow
            icon={ShieldCheck}
            label={req.preAuthorized ? 'Auto-approved (write grant)' : 'Approved'}
            command={req.command}
            reason={req.reason}
          />
        </li>
      );
    }
    case EventType.ApprovalGrant: {
      // Surface a DENY (an allow is already carried by the "Approved" row above).
      const decision = (ev.payload as { decision?: string } | null)?.decision;
      return decision === 'deny' ? (
        <li>
          <AuditRow icon={ShieldQuestion} label="Denied" muted />
        </li>
      ) : null;
    }
    case EventType.SessionStarted:
    case EventType.Status:
      return null;
    default:
      return (
        <li title={formatDateTimeWithSeconds(ev.ts)}>
          <GenericEventRow event={ev} />
        </li>
      );
  }
}

/** The HITL gate: the exact proposed command + its params + the paramsHash the grant is bound to. */
function ApprovalCard({
  req,
  deviceName,
  onDecide,
}: {
  req: ApprovalRequestPayload;
  deviceName?: string;
  onDecide: (d: 'allow' | 'deny') => Promise<void>;
}) {
  const [sent, setSent] = useState<'allow' | 'deny' | null>(null);
  const respond = (d: 'allow' | 'deny') => {
    setSent(d);
    onDecide(d).catch(() => setSent(null)); // re-enable on a failed POST
  };
  const hasParams = req.params && Object.keys(req.params).length > 0;
  const reason = typeof req.reason === 'string' ? req.reason.trim() : '';
  return (
    <div data-testid="troubleshoot-approval" className="rounded-md border border-primary/40 bg-primary/5 p-3">
      <div className="mb-1 flex items-center gap-2 text-sm font-medium text-foreground">
        <ShieldQuestion className="h-4 w-4 text-primary" />
        {'Approve this command?'}
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        {'You are the approver — the customer is not asked. Approve runs exactly this; to change course, Deny and tell the agent what to do instead.'}
      </p>
      {reason ? (
        <p data-testid="troubleshoot-approval-reason" className="mb-2 break-words text-[11px] text-muted-foreground">
          {reason}
        </p>
      ) : null}
      <div className="mb-1 text-[11px] text-muted-foreground">{`Tool: ${toolKey(req.tool)}`}</div>
      <pre className="mb-2 overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-muted p-2 text-xs text-foreground">
        {req.command}
      </pre>
      {hasParams ? (
        <details className="mb-2 text-[11px]">
          <summary className="cursor-pointer text-muted-foreground">{'Parameters'}</summary>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 text-muted-foreground">
            {JSON.stringify(req.params, null, 2)}
          </pre>
        </details>
      ) : null}
      <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
        <dt>{'Runs on'}</dt>
        <dd className="min-w-0 truncate">{deviceName || req.target}</dd>
        <dt>{'Params hash'}</dt>
        <dd className="min-w-0 truncate font-mono" title={req.paramsHash}>
          {req.paramsHash}
        </dd>
        {req.expiresAt ? (
          <>
            <dt>{'Expires'}</dt>
            <dd>{formatDateTimeWithSeconds(req.expiresAt)}</dd>
          </>
        ) : null}
      </dl>
      <div className="flex gap-2">
        <Button size="sm" disabled={sent !== null} onClick={() => respond('allow')}>
          {sent === 'allow' ? 'Approving…' : 'Approve'}
        </Button>
        <Button size="sm" variant="ghost" disabled={sent !== null} onClick={() => respond('deny')}>
          {sent === 'deny' ? 'Denying…' : 'Deny'}
        </Button>
      </div>
    </div>
  );
}

function AuditRow({
  icon: Icon,
  label,
  command,
  reason,
  muted,
}: {
  icon: typeof Activity;
  label: string;
  command?: string;
  reason?: string;
  muted?: boolean;
}) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <span className={cn('font-medium', muted ? 'text-muted-foreground' : 'text-foreground')}>
          {label}
        </span>
        {command ? <span className="ml-2 break-all font-mono text-muted-foreground">{command}</span> : null}
        {reason ? <p className="break-words text-muted-foreground">{reason}</p> : null}
      </div>
    </div>
  );
}

function ToolStepRow({
  item,
  live,
}: {
  item: Extract<TimelineItem, { kind: typeof TimelineKind.Tool }>;
  live: boolean;
}) {
  const key = item.tool ? toolKey(item.tool) : undefined;
  if (key && HIDDEN_TOOL_KEYS.has(key)) return null;
  const isOk = item.status === ToolStatus.Ok;
  const isError = item.status === ToolStatus.Error;
  const spinning = item.status === ToolStatus.Running && live;
  const Icon = isError
    ? AlertTriangle
    : isOk
      ? CheckCircle2
      : spinning
        ? Loader2
        : (key && TOOL_ICON[key]) || ShieldQuestion;
  const output = item.output?.trim();
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon
        className={cn(
          'mt-0.5 h-3.5 w-3.5 shrink-0',
          isError ? 'text-destructive' : isOk ? 'text-muted-foreground' : 'text-primary',
          spinning && 'animate-spin',
        )}
      />
      <ToolStepBody
        label={key ?? 'tool'}
        description={item.description}
        command={item.command}
        output={output}
        failed={isError}
      />
    </div>
  );
}

/** verify / error without a toolCallId / session.ended / anything new. */
function GenericEventRow({ event }: { event: SessionEvent }) {
  const isError = event.type === EventType.Error;
  const ended = event.type === EventType.SessionEnded;
  const text = summarize(event);
  const Icon = isError ? AlertTriangle : ended ? CircleDot : CheckCircle2;
  if (ended) {
    return (
      <div className="flex items-center justify-center gap-1.5 py-1 text-[11px] text-muted-foreground">
        <Icon className="h-3 w-3" />
        {text || 'Session ended'}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', isError ? 'text-destructive' : 'text-muted-foreground')} />
      <div className="min-w-0">
        <span className="font-medium text-foreground">{event.type}</span>
        {text ? <span className="ml-2 break-words text-muted-foreground">{text}</span> : null}
      </div>
    </div>
  );
}

function SessionComposer({ onSend }: { onSend: (text: string) => void }) {
  const [text, setText] = useState('');
  const send = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText('');
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEnterKey(e) && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };
  return (
    <div className="flex items-end gap-2 border-t border-border p-2">
      <textarea
        aria-label="Message the device agent"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        rows={1}
        placeholder="Tell the agent what to check or do next…"
        className="max-h-[100px] min-h-[36px] w-full resize-none rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <Button
        size="icon-md"
        variant="ghost"
        className="shrink-0 text-primary"
        onClick={send}
        disabled={!text.trim()}
        aria-label="Send message"
      >
        <SendHorizonal />
      </Button>
    </div>
  );
}

/** Trailing spinner with the seconds spent on the current step, so a long step never looks hung. */
function WorkingIndicator({ phase, resetKey }: { phase: 'booting' | 'working'; resetKey: number }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    setSecs(0);
    const started = Date.now();
    const id = setInterval(() => setSecs(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [resetKey]);
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>
        {phase === 'booting' ? 'Connecting to the device…' : 'Working…'}
        {secs >= 1 ? <span className="ml-1 tabular-nums opacity-70">{`${secs}s`}</span> : null}
      </span>
    </div>
  );
}

const MD_COMPONENTS = {
  p: ({ children }: { children?: ReactNode }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }: { children?: ReactNode }) => <ul className="mb-2 ml-4 list-disc last:mb-0">{children}</ul>,
  ol: ({ children }: { children?: ReactNode }) => <ol className="mb-2 ml-4 list-decimal last:mb-0">{children}</ol>,
  li: ({ children }: { children?: ReactNode }) => <li className="mb-1">{children}</li>,
  code: ({ children }: { children?: ReactNode }) => <code className="rounded bg-muted px-1 py-0.5">{children}</code>,
  pre: ({ children }: { children?: ReactNode }) => (
    <pre className="mb-2 overflow-x-auto rounded bg-muted p-2 last:mb-0">{children}</pre>
  ),
  table: ({ children }: { children?: ReactNode }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="min-w-full">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: ReactNode }) => (
    <th className="border-b border-border px-2 py-1 text-left font-medium">{children}</th>
  ),
  td: ({ children }: { children?: ReactNode }) => <td className="border-b border-border px-2 py-1">{children}</td>,
};

function AgentMessage({ text, streaming }: { text: string; streaming: boolean }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <Activity className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
      <div className="min-w-0 break-words text-foreground">
        <Markdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {text}
        </Markdown>
        {streaming ? (
          <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-primary align-middle" aria-hidden />
        ) : null}
      </div>
    </div>
  );
}

function summarize(event: SessionEvent): string {
  const p = event.payload as Record<string, unknown> | null;
  if (!p || typeof p !== 'object') return '';
  for (const k of ['text', 'command', 'message', 'note', 'status', 'decision']) {
    if (typeof p[k] === 'string') return p[k] as string;
  }
  return '';
}
